import React, { useState, useEffect } from 'react';

/**
 * Debug component to diagnose voice recording issues
 * Shows detailed browser capability information
 */
const VoiceRecorderDebug = () => {
  const [debugInfo, setDebugInfo] = useState({});

  useEffect(() => {
    const info = {
      // Browser detection
      userAgent: navigator.userAgent,
      browserName: getBrowserName(),

      // API availability
      hasNavigator: typeof navigator !== 'undefined',
      hasMediaDevices: typeof navigator !== 'undefined' && !!navigator.mediaDevices,
      hasGetUserMedia: typeof navigator !== 'undefined' &&
                       navigator.mediaDevices &&
                       typeof navigator.mediaDevices.getUserMedia === 'function',
      hasMediaRecorder: typeof window !== 'undefined' && typeof window.MediaRecorder === 'function',

      // Context information
      protocol: window.location.protocol,
      hostname: window.location.hostname,
      port: window.location.port,
      fullUrl: window.location.href,

      // Security context
      isSecureContext: window.isSecureContext,
      isLocalhost: window.location.hostname === 'localhost',
      is127001: window.location.hostname === '127.0.0.1',

      // MediaRecorder support details
      mediaRecorderSupported: checkMediaRecorderSupport(),
    };

    setDebugInfo(info);
    console.log('Voice Recorder Debug Info:', info);
  }, []);

  function getBrowserName() {
    const ua = navigator.userAgent;
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
    return 'Unknown';
  }

  function checkMediaRecorderSupport() {
    if (typeof window.MediaRecorder === 'undefined') {
      return 'Not available';
    }

    const types = [
      'audio/webm',
      'audio/webm;codecs=opus',
      'audio/ogg;codecs=opus',
      'audio/mp4',
    ];

    const supported = types.filter(type => MediaRecorder.isTypeSupported(type));
    return supported.length > 0 ? supported.join(', ') : 'No formats supported';
  }

  const getStatus = (value) => (value ? '✅' : '❌');
  const getColor = (value) => (value ? '#22c55e' : '#ef4444');

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>🔍 Voice Recording Debug Information</h2>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Browser Information</h3>
        <div style={styles.row}>
          <span style={styles.label}>Browser:</span>
          <span style={styles.value}>{debugInfo.browserName}</span>
        </div>
        <div style={styles.row}>
          <span style={styles.label}>User Agent:</span>
          <span style={{...styles.value, fontSize: '12px', wordBreak: 'break-all'}}>
            {debugInfo.userAgent}
          </span>
        </div>
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>API Availability</h3>
        <div style={styles.row}>
          <span style={styles.label}>navigator exists:</span>
          <span style={{...styles.value, color: getColor(debugInfo.hasNavigator)}}>
            {getStatus(debugInfo.hasNavigator)} {String(debugInfo.hasNavigator)}
          </span>
        </div>
        <div style={styles.row}>
          <span style={styles.label}>navigator.mediaDevices:</span>
          <span style={{...styles.value, color: getColor(debugInfo.hasMediaDevices)}}>
            {getStatus(debugInfo.hasMediaDevices)} {String(debugInfo.hasMediaDevices)}
          </span>
        </div>
        <div style={styles.row}>
          <span style={styles.label}>getUserMedia:</span>
          <span style={{...styles.value, color: getColor(debugInfo.hasGetUserMedia)}}>
            {getStatus(debugInfo.hasGetUserMedia)} {String(debugInfo.hasGetUserMedia)}
          </span>
        </div>
        <div style={styles.row}>
          <span style={styles.label}>MediaRecorder:</span>
          <span style={{...styles.value, color: getColor(debugInfo.hasMediaRecorder)}}>
            {getStatus(debugInfo.hasMediaRecorder)} {String(debugInfo.hasMediaRecorder)}
          </span>
        </div>
        <div style={styles.row}>
          <span style={styles.label}>Supported formats:</span>
          <span style={styles.value}>{debugInfo.mediaRecorderSupported}</span>
        </div>
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Security Context</h3>
        <div style={styles.row}>
          <span style={styles.label}>Protocol:</span>
          <span style={{...styles.value, color: debugInfo.protocol === 'https:' ? '#22c55e' : '#f59e0b'}}>
            {debugInfo.protocol}
          </span>
        </div>
        <div style={styles.row}>
          <span style={styles.label}>Hostname:</span>
          <span style={{...styles.value, color: getColor(debugInfo.isLocalhost)}}>
            {debugInfo.hostname}
          </span>
        </div>
        <div style={styles.row}>
          <span style={styles.label}>Port:</span>
          <span style={styles.value}>{debugInfo.port || '(default)'}</span>
        </div>
        <div style={styles.row}>
          <span style={styles.label}>Full URL:</span>
          <span style={{...styles.value, fontSize: '12px', wordBreak: 'break-all'}}>
            {debugInfo.fullUrl}
          </span>
        </div>
        <div style={styles.row}>
          <span style={styles.label}>Secure Context:</span>
          <span style={{...styles.value, color: getColor(debugInfo.isSecureContext)}}>
            {getStatus(debugInfo.isSecureContext)} {String(debugInfo.isSecureContext)}
          </span>
        </div>
        <div style={styles.row}>
          <span style={styles.label}>Is "localhost":</span>
          <span style={{...styles.value, color: getColor(debugInfo.isLocalhost)}}>
            {getStatus(debugInfo.isLocalhost)} {String(debugInfo.isLocalhost)}
          </span>
        </div>
        <div style={styles.row}>
          <span style={styles.label}>Is "127.0.0.1":</span>
          <span style={{...styles.value, color: debugInfo.is127001 ? '#f59e0b' : '#999'}}>
            {debugInfo.is127001 ? '⚠️' : '—'} {String(debugInfo.is127001)}
          </span>
        </div>
      </div>

      <div style={styles.diagnosis}>
        <h3 style={styles.diagnosisTitle}>📋 Diagnosis</h3>
        {getDiagnosis(debugInfo)}
      </div>
    </div>
  );

  function getDiagnosis(info) {
    const issues = [];
    const recommendations = [];

    // Check API support
    if (!info.hasMediaDevices) {
      issues.push('❌ navigator.mediaDevices is not available');
      recommendations.push('Your browser is too old or doesn\'t support WebRTC. Update your browser.');
    }

    if (!info.hasGetUserMedia) {
      issues.push('❌ getUserMedia is not available');
      recommendations.push('Your browser doesn\'t support microphone access. Try Chrome, Firefox, or Edge.');
    }

    if (!info.hasMediaRecorder) {
      issues.push('❌ MediaRecorder is not available');
      recommendations.push('Your browser doesn\'t support audio recording. Update to the latest version.');
    }

    // Check security context
    if (!info.isSecureContext) {
      issues.push('❌ Not in a secure context (requires HTTPS or localhost)');
      if (info.protocol === 'http:' && !info.isLocalhost) {
        recommendations.push('🔴 CRITICAL: You must access via "localhost" not an IP address!');
        recommendations.push(`Current: ${info.fullUrl}`);
        recommendations.push('Change to: http://localhost' + (info.port ? ':' + info.port : ''));
      }
    }

    if (info.is127001) {
      issues.push('⚠️ Accessing via 127.0.0.1 instead of localhost');
      recommendations.push('🔴 Change URL from 127.0.0.1 to localhost');
      recommendations.push('Only "localhost" is treated as secure, NOT "127.0.0.1"');
      recommendations.push('Change to: http://localhost' + (info.port ? ':' + info.port : ''));
    }

    if (info.protocol === 'http:' && !info.isLocalhost && !info.is127001) {
      issues.push('❌ HTTP protocol on non-localhost domain');
      recommendations.push('You must use HTTPS for non-localhost domains');
      recommendations.push('See TROUBLESHOOTING.md for HTTPS setup instructions');
    }

    // All good?
    if (issues.length === 0) {
      return (
        <div style={styles.successBox}>
          <div style={styles.successIcon}>✅</div>
          <div style={styles.successText}>
            All checks passed! Voice recording should work.
          </div>
          <div style={styles.successNote}>
            If it still doesn't work, check if you've granted microphone permissions.
          </div>
        </div>
      );
    }

    return (
      <>
        <div style={styles.issuesBox}>
          <strong>Issues Found:</strong>
          <ul style={styles.list}>
            {issues.map((issue, i) => (
              <li key={i}>{issue}</li>
            ))}
          </ul>
        </div>

        <div style={styles.recommendationsBox}>
          <strong>How to Fix:</strong>
          <ol style={styles.list}>
            {recommendations.map((rec, i) => (
              <li key={i}>{rec}</li>
            ))}
          </ol>
        </div>
      </>
    );
  }
};

const styles = {
  container: {
    maxWidth: '800px',
    margin: '20px auto',
    padding: '20px',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    marginBottom: '20px',
    color: '#333',
    textAlign: 'center',
  },
  section: {
    marginBottom: '20px',
    padding: '16px',
    background: '#f9fafb',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '12px',
    color: '#111827',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '8px 0',
    borderBottom: '1px solid #e5e7eb',
    gap: '16px',
  },
  label: {
    fontWeight: '500',
    color: '#6b7280',
    minWidth: '180px',
  },
  value: {
    fontFamily: 'monospace',
    color: '#111827',
    flex: 1,
    textAlign: 'right',
  },
  diagnosis: {
    marginTop: '24px',
    padding: '20px',
    background: '#fef3c7',
    borderRadius: '8px',
    border: '2px solid #fbbf24',
  },
  diagnosisTitle: {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '16px',
    color: '#92400e',
  },
  issuesBox: {
    background: '#fee2e2',
    padding: '16px',
    borderRadius: '8px',
    marginBottom: '16px',
    border: '1px solid #fca5a5',
    color: '#991b1b',
  },
  recommendationsBox: {
    background: '#dbeafe',
    padding: '16px',
    borderRadius: '8px',
    border: '1px solid #93c5fd',
    color: '#1e40af',
  },
  successBox: {
    background: '#d1fae5',
    padding: '20px',
    borderRadius: '8px',
    border: '2px solid #6ee7b7',
    textAlign: 'center',
  },
  successIcon: {
    fontSize: '48px',
    marginBottom: '12px',
  },
  successText: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#065f46',
    marginBottom: '8px',
  },
  successNote: {
    fontSize: '14px',
    color: '#047857',
  },
  list: {
    marginTop: '12px',
    paddingLeft: '20px',
    lineHeight: '1.8',
  },
};

export default VoiceRecorderDebug;
